import { AppLayout } from "@/components/layout";
import { apiFetch } from "@/lib/api";
import {
  Bell, Calendar, DollarSign, AlertTriangle, Info,
  Video, MessageSquare, CreditCard, Star, Clock,
  Check, CheckCheck,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow, format } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";

/* ── same helper as bell ──────────────────────────────────────────────── */
function getNotifMeta(type: string): { Icon: any; colour: string; category: string } {
  if (type.startsWith("BOOKING") || type === "DROPIN_BOOKING_RECEIVED") {
    return { Icon: Calendar, colour: "text-blue-400 bg-blue-500/15 border-blue-500/20", category: "sessions" };
  }
  if (type.startsWith("VIDEO")) {
    return { Icon: Video, colour: "text-purple-400 bg-purple-500/15 border-purple-500/20", category: "sessions" };
  }
  if (type === "SESSION_REMINDER") {
    return { Icon: Clock, colour: "text-amber-400 bg-amber-500/15 border-amber-500/20", category: "sessions" };
  }
  if (type.startsWith("PAYMENT") || type.startsWith("PACKAGE") || type.startsWith("SUBSCRIPTION")) {
    return { Icon: DollarSign, colour: "text-emerald-400 bg-emerald-500/15 border-emerald-500/20", category: "payments" };
  }
  if (type.startsWith("MEMBERSHIP")) {
    return { Icon: CreditCard, colour: "text-violet-400 bg-violet-500/15 border-violet-500/20", category: "payments" };
  }
  if (type === "RATING_RECEIVED") {
    return { Icon: Star, colour: "text-yellow-400 bg-yellow-500/15 border-yellow-500/20", category: "other" };
  }
  if (type === "CHAT_MESSAGE_NEW") {
    return { Icon: MessageSquare, colour: "text-sky-400 bg-sky-500/15 border-sky-500/20", category: "other" };
  }
  if (type === "CALENDAR_SYNC_FAILED" || type.includes("FAILED") || type.includes("CANCELLED")) {
    return { Icon: AlertTriangle, colour: "text-red-400 bg-red-500/15 border-red-500/20", category: "other" };
  }
  return { Icon: Info, colour: "text-muted-foreground bg-white/5 border-white/10", category: "other" };
}

type Tab = "all" | "unread" | "sessions" | "payments" | "other";

export default function NotificationsPage() {
  const [tab, setTab] = useState<Tab>("all");
  const [markingAll, setMarkingAll] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["notifications-page"],
    queryFn: () => apiFetch<{ notifications: any[]; unreadCount: number }>("/notifications/my?limit=100"),
    retry: false,
  });

  const all = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  const filtered = all.filter((n: any) => {
    if (tab === "unread") return !n.isRead;
    if (tab === "sessions") return getNotifMeta(n.type).category === "sessions";
    if (tab === "payments") return getNotifMeta(n.type).category === "payments";
    if (tab === "other")   return getNotifMeta(n.type).category === "other";
    return true;
  });

  async function markRead(id: string) {
    try {
      await apiFetch(`/notifications/${id}/read`, { method: "PUT" });
      refetch();
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch {}
  }

  async function markAllRead() {
    setMarkingAll(true);
    try {
      await apiFetch("/notifications/read-all", { method: "PUT" });
      refetch();
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch {}
    setMarkingAll(false);
  }

  const tabs: { key: Tab; label: string; count?: number }[] = [
    { key: "all",      label: "All",      count: all.length },
    { key: "unread",   label: "Unread",   count: unreadCount },
    { key: "sessions", label: "Sessions" },
    { key: "payments", label: "Payments" },
    { key: "other",    label: "Other" },
  ];

  return (
    <AppLayout>
      <div className="flex flex-col gap-6 pb-12 max-w-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-1">Notifications</h1>
            <p className="text-muted-foreground">
              {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={markingAll}
              onClick={markAllRead}
              className="border-white/10 hover:bg-white/5 text-muted-foreground shrink-0"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 flex-wrap border-b border-white/10 pb-4">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                tab === t.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-white/5"
              )}
            >
              {t.label}
              {t.count !== undefined && (
                <span className="ml-1.5 font-mono opacity-70">({t.count})</span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-30" />
            <p className="text-white font-medium text-lg">
              {tab === "unread" ? "No unread notifications" : `No ${tab} notifications`}
            </p>
            <p className="text-muted-foreground text-sm mt-1">
              {tab === "unread" ? "You're all caught up!" : "Nothing here yet"}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((n: any) => {
              const { Icon, colour } = getNotifMeta(n.type);
              const date = new Date(n.createdAt);
              const isRecent = Date.now() - date.getTime() < 1000 * 60 * 60 * 24;

              return (
                <div
                  key={n.id}
                  className={cn(
                    "flex items-start gap-4 p-4 rounded-xl border transition-all",
                    n.isRead
                      ? "bg-white/[0.02] border-white/5"
                      : "bg-primary/[0.04] border-primary/10"
                  )}
                >
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center shrink-0 border",
                    colour
                  )}>
                    <Icon className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      "text-sm leading-snug",
                      n.isRead ? "text-muted-foreground" : "text-white font-medium"
                    )}>
                      {n.message}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1.5">
                      {isRecent
                        ? formatDistanceToNow(date, { addSuffix: true })
                        : format(date, "MMM d, yyyy · h:mm a")}
                    </p>
                  </div>

                  {!n.isRead && (
                    <button
                      onClick={() => markRead(n.id)}
                      className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
